import { useEffect, useRef, useState } from 'react';

/**
 * Owns the singleton HTMLAudioElement + play/stop lifecycle for the OSO
 * live stream. Extracted from PlayerContext so state ownership, DOM
 * side-effects, and metadata polling can be reasoned about independently.
 */
export function useAudioStream(streamUrl) {
  const audioRef = useRef(null);
  const [playing, setPlaying] = useState(false);
  const [loading, setLoading] = useState(false);
  const [volume, setVolume] = useState(0.85);

  useEffect(() => {
    const a = new Audio();
    a.preload = 'none';
    a.crossOrigin = 'anonymous';
    audioRef.current = a;
    return () => {
      a.pause();
      a.src = '';
    };
  }, []);

  useEffect(() => {
    if (audioRef.current) audioRef.current.volume = volume;
  }, [volume]);

  const play = () => {
    const a = audioRef.current;
    if (!a || !streamUrl) return;
    setLoading(true);
    a.src = `${streamUrl}?_=${Date.now()}`;
    a.load();
    const p = a.play();
    if (p && p.then) {
      p.then(() => {
        setPlaying(true);
        setLoading(false);
      }).catch(() => {
        setPlaying(false);
        setLoading(false);
      });
    } else {
      setPlaying(true);
      setLoading(false);
    }
  };

  const stop = () => {
    const a = audioRef.current;
    if (!a) return;
    a.pause();
    a.src = '';
    setPlaying(false);
    setLoading(false);
  };

  const toggle = () => {
    if (playing) stop();
    else play();
  };

  return { playing, loading, volume, setVolume, play, stop, toggle };
}
